// react
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

// models
import { GdprDetails, FieldType, ReferralFormType } from '@karehero/models';

// components
import { Card } from '@/components/atoms/Card/Card';
import { Title } from '@/components/atoms/Title/Title';

import { createFieldHelper } from '@/components/organisms/EditorFields/fieldHelper';
import { EditorFields } from '@/components/organisms/EditorFields/EditorFields';

const sjpGdprFields = createFieldHelper<GdprDetails>({
  gdprSelections: {
    label: '',
    ariaLabel: 'gdpr-selection',
    field: {
      type: FieldType.MultiSelectBox,
      variant: 'default',
      options: [
        {
          value: 'communication-consent',
          label:
            'I have received consent from the referred client to receive communications from KareHero.',
        },
        {
          value: 'data-share-consent',
          label:
            'I have received consent from the referred client that they would like KareHero to share relevant information about their care needs with me.',
        },
      ],
    },
  },
});

const ifaGdprFields = createFieldHelper<GdprDetails>({
  gdprSelections: {
    label: '',
    ariaLabel: 'gdpr-selection',
    field: {
      type: FieldType.MultiSelectBox,
      variant: 'default',
      options: [
        {
          value: 'communication-consent-ifa',
          label:
            'I have received consent from the referred client to receive communications from KareHero.',
        },
        {
          value: 'data-share-consent-ifa',
          label:
            'I have received consent from the referred client that they would like KareHero to share relevant information about their care needs with me.',
        },
      ],
    },
  },
});

const SjpGdprConsent = ({ value, setValue }: GdprConsentProps) => {
  return (
    <Wrapper>
      <Title isNoMargin>GDPR notice and consent</Title>
      <EditorFields fields={sjpGdprFields} value={value} onChange={setValue} />
      <Description>
        For more information on our privacy practices and how we are committed
        to protecting and respecting your privacy, please review our{' '}
        <StyledInternalLink
          to={`https://www.karehero.com/privacy-policy`}
          target='_blank'
          rel='noopener noreferrer'
        >
          {'Privacy Policy'}
        </StyledInternalLink>
        .
      </Description>
      <Description>
        By submitting you consent to allow KareHero to store and process the
        personal information provided to deliver the requested service.
      </Description>
    </Wrapper>
  );
};

const IfaGdprConsent = ({ value, setValue }: GdprConsentProps) => {
  return (
    <Wrapper>
      <GdprContainer>
        <Card
          key='fee-description'
          color='yellow'
          isBordered={false}
          isPaddingSm
        >
          <GdprPanel>
            <SubTitle>GDPR and Consent</SubTitle>
            <Description>
              We are committed to protecting and respecting your privacy. You
              can read more about our Privacy Policy{' '}
              <StyledInternalLink
                to={`https://www.karehero.com/privacy-policy`}
                target='_blank'
                rel='noopener noreferrer'
              >
                here
              </StyledInternalLink>
              .
            </Description>
            <EditorFields
              fields={ifaGdprFields}
              value={value}
              onChange={setValue}
            />
          </GdprPanel>
        </Card>
      </GdprContainer>
    </Wrapper>
  );
};

export const GdprConsent = ({ formType, ...props }: GdprConsentProps) => {
  if (formType === ReferralFormType.SjpIFA) {
    return <SjpGdprConsent formType={formType} {...props} />;
  }

  return <IfaGdprConsent formType={formType} {...props} />;
};

// styles

const GdprContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: space-between;
  gap: ${(props) => props.theme.spacing[24]};
  ${(props) =>
    props.theme.breakpoint.md(css`
      flex-direction: row;
      align-items: stretch;
    `)}
`;

const SubTitle = styled.div`
  font: ${(props) => props.theme.typography.heading['2xl']};
  color: ${(props) => props.theme.color.neutral[100]};
  text-align: left;
`;

const Description = styled.div`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[100]};
  text-align: left;
`;

const GdprPanel = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 200px;
  gap: ${(props) => props.theme.spacing[8]};
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: ${(props) => props.theme.spacing[16]};
`;

const StyledInternalLink = styled(Link)`
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[100]};
  text-decoration: underline;
`;

// types
interface GdprConsentProps {
  formType: ReferralFormType;
  value: Partial<GdprDetails>;
  setValue: (value: Partial<GdprDetails>) => void;
}

GdprConsent.displayName = 'GdprConsent';

export default GdprConsent;
